import React, { useEffect, useState } from 'react';
import Button from '@atlaskit/button/standard-button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import Textfield from '@atlaskit/textfield';

import { DISABLE, ENABLE, MAX_NESTING_LEVEL, TOGGLE_STATE_CHANGED, VALUE_CHANGED } from '../../../utils/constants';
import CircleGroupIcon from '../../common/icons/CircleGroupIcon';

import { OptionItemProperties } from './interfaces';
import OptionItemTabs from './OptionItemTabs';
import {
    ExtraOption,
    ItemDefaultOption,
    ItemExtraOptionsWrapper,
    ItemNameWrapper,
    ItemWrapper,
    OptionsWrapper,
    RemoveOption,
} from './OptionsAdderStyled';

const OptionItem: React.FC<OptionItemProperties> = ({
    onChange,
    value,
    isDefaultOption,
    isEnabled = true,
    optionsWithUpdatedName,
    shouldAutoFocus,
    optionIndex,
    optionKey,
    translation,
    onConfigureOptionsClick,
    handleDeleteOptionClick,
    nestingLevelChanged,
    currentNestingLevel,
    locale,
    provided,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const isNestingEligibleForOption = (optionValue: string): boolean => !!optionValue;

    const isLastLevel = (): boolean => currentNestingLevel === MAX_NESTING_LEVEL;

    useEffect(() => {
        if (nestingLevelChanged) {
            setIsExpanded(false);
        }
    }, [nestingLevelChanged]);

    return (
        <>
            <ItemWrapper>
                <OptionsWrapper>
                    <CircleGroupIcon />
                    <ItemNameWrapper>
                        {isDefaultOption && <ItemDefaultOption>(default)</ItemDefaultOption>}
                        <Textfield
                            onChange={(e: React.FormEvent) =>
                                onChange(
                                    (e.target as HTMLInputElement).value,
                                    VALUE_CHANGED,
                                    optionIndex,
                                    optionsWithUpdatedName
                                )
                            }
                            onBlur={(e: React.FormEvent) => {
                                const trimmedValue = (e.target as HTMLInputElement).value.trim();
                                onChange(trimmedValue, VALUE_CHANGED, optionIndex, optionsWithUpdatedName);
                            }}
                            value={value}
                            placeholder='Option'
                            autoFocus={shouldAutoFocus}
                            testId='option-input'
                        />
                    </ItemNameWrapper>
                    <ItemExtraOptionsWrapper>
                        {!isLastLevel() && (
                            <ExtraOption
                                className='text-option'
                                onClick={() => isNestingEligibleForOption(value) && onConfigureOptionsClick(optionKey)}
                            >
                                Options
                            </ExtraOption>
                        )}
                        <ExtraOption
                            className='text-option'
                            onClick={() => onChange(!isEnabled, TOGGLE_STATE_CHANGED, optionIndex, optionsWithUpdatedName)}
                        >
                            {isEnabled ? DISABLE : ENABLE}
                        </ExtraOption>
                        <ExtraOption onClick={() => setIsExpanded(!isExpanded)}>
                            {isExpanded ? (
                                <ChevronDownIcon
                                    label='arrow-down'
                                    size='medium'
                                />
                            ) : (
                                <ChevronRightIcon
                                    label='arrow-right'
                                    size='medium'
                                />
                            )}
                        </ExtraOption>
                    </ItemExtraOptionsWrapper>
                </OptionsWrapper>
                {isExpanded && (
                    <OptionItemTabs
                        optionKey={optionKey}
                        isDefaultOption={isDefaultOption}
                        onChange={onChange}
                        optionIndex={optionIndex}
                        optionValue={value}
                        translation={translation}
                        locale={locale}
                        provided={provided}
                    />
                )}
                {isExpanded && (
                    <RemoveOption>
                        <Button onClick={() => handleDeleteOptionClick(value, optionKey, optionIndex)}>Delete</Button>
                    </RemoveOption>
                )}
            </ItemWrapper>
        </>
    );
};

export default OptionItem;
